import * as React from 'react';
import * as Elements from './Accordion.styles';
import { Header } from './components/Header';
import { reducer, getAction } from './state';
import { useActions } from './hooks';
function getHeight(ref) {
    if (ref.current === null)
        return 0;
    return ref.current.getBoundingClientRect().height;
}
var AccordionContext = React.createContext({
    status: 'closed',
    setOpen: function () { },
    toggleOpen: function () { }
});
export var useAccordion = function () { return React.useContext(AccordionContext); };
export var Accordion = function (_a) {
    var isOpen = _a.isOpen, initialState = _a.initialState, onChange = _a.onChange, renderHeader = _a.renderHeader, label = _a.label, children = _a.children, testId = _a.testId;
    var _b = React.useReducer(reducer, {
        status: 'closed',
        height: 0
    }), state = _b[0], dispatch = _b[1];
    var isFirstRenderRef = React.useRef(true);
    var containerRef = React.useRef(null);
    var innerRef = React.useRef(null);
    var headerRef = React.useRef(null);
    var _c = useActions(state, innerRef, dispatch), toggleOpen = _c.toggleOpen, setOpen = _c.setOpen;
    /**
     * Handle initial and controlled state
     */
    React.useEffect(function handleControlledToggle() {
        /**
         * set initial state on first render and skip `isOpen` evaluation
         * If set to 'open', this will skip the initial transition animation
         */
        if (initialState !== undefined && isFirstRenderRef.current) {
            dispatch({
                type: getAction(initialState),
                height: getHeight(innerRef)
            });
            return;
        }
        /**
         * Response to `isOpen` changes by opening or closing
         */
        if (isOpen !== undefined && isOpen) {
            dispatch({
                type: 'SET_OPENING',
                height: getHeight(innerRef)
            });
        }
        if (isOpen !== undefined && !isOpen) {
            dispatch({ type: 'SET_CLOSING' });
        }
    }, [isOpen]);
    /**
     * Set internal state on toggle (uncontrolled)
     * Update content height to show/hide drawer
     * Call onChange with updated state
     */
    React.useEffect(function handleUncontrolledToggle() {
        var timeout;
        if (state.status === 'opening') {
            timeout = setTimeout(function () {
                dispatch({
                    type: 'SET_OPEN',
                    height: getHeight(innerRef)
                });
            }, Elements.TRANSITION_DURATION);
        }
        if (state.status === 'closing') {
            timeout = setTimeout(function () {
                dispatch({ type: 'SET_CLOSED' });
            }, Elements.TRANSITION_DURATION);
        }
        /**
         * Skip onChange execution on first render
         */
        if (onChange !== undefined && !isFirstRenderRef.current) {
            onChange(state.status);
        }
        return function () {
            clearTimeout(timeout);
        };
    }, [state.status]);
    /**
     * Watch for dynamic height changes to Content and
     * update height of parent accordingly.  This accounts
     * for dynamical height content, such as validation messages
     * in forms.
     */
    React.useEffect(function () {
        var ResizeObserver = global.ResizeObserver;
        var resizeObserver = new ResizeObserver(function (entries) {
            if (!Array.isArray(entries))
                return;
            if (entries.length < 1)
                return;
            var entry = entries[0];
            if (state.height !== entry.contentRect.height &&
                state.status === 'open') {
                dispatch({ type: 'SET_HEIGHT', height: entry.contentRect.height });
            }
        });
        var element = innerRef.current;
        if (element !== null) {
            resizeObserver.observe(element);
            return function () { return resizeObserver.unobserve(element); };
        }
        return function () { };
    }, [state.status, state.height]);
    /**
     * Set isFirstRenderRef to false after first render
     */
    React.useEffect(function () {
        isFirstRenderRef.current = false;
    }, []);
    return (React.createElement(AccordionContext.Provider, { value: {
            status: state.status,
            setOpen: setOpen,
            toggleOpen: toggleOpen
        } },
        React.createElement(Elements.Container, { status: state.status, ref: containerRef },
            React.createElement(Elements.Head, { ref: headerRef },
                label !== undefined && React.createElement(Header, { label: label }),
                renderHeader !== undefined && renderHeader),
            React.createElement(Elements.Drawer, { "$height": state.height, status: state.status, "data-testid": testId },
                React.createElement(Elements.Content, { ref: innerRef }, children)))));
};
//# sourceMappingURL=Accordion.js.map